import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Card, Tabs, Modal, Form, Row, Col, Space, Typography, DatePicker, InputNumber } from 'antd';
import cargarDeptosMunicipios from '../../../../services/departMunic/deptosMunicipiosServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPhone, faEnvelope, faGraduationCap, faFileAlt, faIdCard, faSave, faTimes, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Option } = Select;
const { Title, Text } = Typography;
const { Item } = Form;

const defaultData = {
  nombre_matriculado: '',
  identificacion: '',
  tipo_documento: null,
  telefono: '',
  email: '',
  sexo: null,
  fecha_nacimiento: dayjs().subtract(18, 'year'),
  tratamiento_datos: null,
  oid_municipio: null,
  oid_departamento: null,
  cohorte: null,
  activo: null,
  oid_fc_dhaa_grupo_etnico: null,
  oid_ct_nombre_instancia: null,
  certificado: null,
  observacion: ''
};

export function ParticipanteForm({ open, onClose, onSave, participante, instancias, loading }) {
  const [activeTab, setActiveTab] = useState("personal");
  const [form] = Form.useForm();
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loadingGeoData, setLoadingGeoData] = useState(false);
  const [selectedDepto, setSelectedDepto] = useState(null);
  const [formData, setFormData] = useState(defaultData);
  const [errors, setErrors] = useState({});

  const findMunicipioById = (municipioId, deptos) => {
    for (const depto of deptos) {
      const municipio = depto.municipios.find(m => m.oid === municipioId);
      if (municipio) {
        return {
          deptoId: depto.oid,
          deptoMunicipios: depto.municipios
        };
      }
    }
    return null;
  };

  const handleDeptoChange = (deptoId) => {
    setSelectedDepto(deptoId);
    const deptoSeleccionado = departamentos.find(d => d.oid === deptoId);
    if (deptoSeleccionado) {
      setMunicipios(deptoSeleccionado.municipios);
      form.setFieldsValue({ oid_municipio: null });
    }
  };

  useEffect(() => {
    const fetchGeoData = async () => {
      setLoadingGeoData(true);
      try {
        const { data } = await cargarDeptosMunicipios();
        const deptosData = data.map(depto => ({
          oid: depto.oid_departamento,
          nombre: depto.nombre_departamento,
          municipios: depto.municipios.map(mun => ({
            oid: mun.oid_municipio,
            nombre: mun.nombre_municipio
          }))
        }));
        setDepartamentos(deptosData);
        if (participante && participante.oid_municipio) {
          const municipioSeleccionado = findMunicipioById(participante.oid_municipio, deptosData);
          if (municipioSeleccionado) {
            const { deptoId, deptoMunicipios } = municipioSeleccionado;
            setSelectedDepto(deptoId);
            setMunicipios(deptoMunicipios);
            setTimeout(() => {
              form.setFieldsValue({
                oid_departamento: deptoId,
                oid_municipio: participante.oid_municipio
              });
            }, 0);
          }
        }
      } catch (error) {
        console.error('Error al obtener datos geográficos:', error);
      } finally {
        setLoadingGeoData(false);
      }
    };

    fetchGeoData();
  }, [participante]);

  useEffect(() => {
    if (participante) {
      const data = {
        fecha_nacimiento: participante.fecha_nacimiento
          ? dayjs(String(participante.fecha_nacimiento), 'YYYY')
          : null,
        nombre_matriculado: participante.nombre_matriculado,
        identificacion: participante.identificacion,
        tipo_documento: participante.tipo_documento,
        telefono: participante.telefono,
        email: participante.email,
        sexo: participante.sexo,
        edad: participante.edad,
        tratamiento_datos: participante.tratamiento_datos.toString(),
        oid_municipio: participante.oid_municipio,
        cohorte: participante.cohorte.toString(),
        activo: participante.activo.toString(),
        oid_fc_dhaa_grupo_etnico: participante.oid_fc_dhaa_grupo_etnico,
        oid_ct_nombre_instancia: participante.oid_ct_nombre_instancia,
        certificado: participante.certificado.toString(),
        observacion: participante.observacion
      };
      setFormData(data);
      requestAnimationFrame(() => {
        form.setFieldsValue(data);
      });
    } else {
      const defaultData = {
        nombre_matriculado: '',
        identificacion: '',
        tipo_documento: null,
        telefono: '',
        email: '',
        sexo: null,
        edad: '',
        fecha_nacimiento: '',
        tratamiento_datos: null,
        oid_municipio: null,
        oid_departamento: null,
        cohorte: null,
        activo: null,
        oid_fc_dhaa_grupo_etnico: null,
        oid_ct_nombre_instancia: null,
        certificado: null,
        observacion: ''
      };

      setFormData(defaultData);
      requestAnimationFrame(() => {
        form.setFieldsValue(defaultData);
      });
    }
    setErrors({});
    setActiveTab("personal");
  }, [participante, open]);

  const validateForm = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      const errorFields = error.errorFields.map(field => field.name[0]);

      const tabMapping = {
        personal: ['nombre_matriculado', 'identificacion', 'tipo_documento', 'sexo', 'edad',
          'fecha_nacimiento', 'oid_municipio', 'oid_departamento', 'oid_fc_dhaa_grupo_etnico'],
        contacto: ['telefono', 'email'],
        academica: ['cohorte', 'activo', 'certificado', 'tratamiento_datos', 'oid_ct_nombre_instancia'],
        observaciones: ['observacion']
      };
      for (const [tab, fields] of Object.entries(tabMapping)) {
        if (fields.some(field => errorFields.includes(field))) {
          setActiveTab(tab);
          break;
        }
      }
      return false;
    }
  };


  const isFormValid = () => {
    const values = form.getFieldsValue();
    const requiredFields = [
      'nombre_matriculado',
      'identificacion',
      'tipo_documento',
      'telefono',
      'email',
      'sexo',
      'edad',
      'fecha_nacimiento',
      'tratamiento_datos',
      'oid_municipio',
      'cohorte',
      'activo',
      'oid_fc_dhaa_grupo_etnico',
      'oid_ct_nombre_instancia',
      'certificado'
    ];

    return requiredFields.every(field => {
      const value = values[field];
      return value !== undefined && value !== null && value !== '';
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!await validateForm()) {
      return;
    }
    try {
      const values = await form.validateFields();
      const dataToSave = {
        ...values,
        fecha_nacimiento: values.fecha_nacimiento ? values.fecha_nacimiento.year() : null
      };
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error al guardar participante:', error);
    }
  };

  const hasErrorsInTab = (tabName) => {
    if (!form || typeof form.getFieldsError !== 'function') {
      return false;
    }

    try {
      const fields = form.getFieldsError();
      switch (tabName) {
        case 'personal':
          return fields.some(f =>
            f.name && ['nombre_matriculado', 'identificacion', 'edad'].includes(f.name[0]) && f.errors?.length > 0
          );
        case 'contacto':
          return fields.some(f =>
            f.name && ['telefono', 'email'].includes(f.name[0]) && f.errors?.length > 0
          );
        default:
          return false;
      }
    } catch (error) {
      console.error('Error checking form errors:', error);
      return false;
    }
  };

  const tabStyle = {
    padding: '0 16px',
    margin: '0 -16px',
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <FontAwesomeIcon icon={faUser} style={{ fontSize: '18px' }} />
          <Title level={4} style={{ margin: 0 }}>
            {participante ? 'Editar Participante' : 'Crear Participante'}
          </Title>
        </Space>
      }
      width="80%"
      style={{ maxWidth: '900px', top: 20 }}
      styles={{
        body: { padding: '24px 0' }
      }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
          <Button onClick={onClose} style={{ minWidth: 100 }}>
            <FontAwesomeIcon icon={faTimes} style={{ marginRight: 8 }} />
            Cancelar
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={!isFormValid()}
            style={{ minWidth: 100 }}
          >
            <FontAwesomeIcon icon={faSave} style={{ marginRight: 8 }} />
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={formData}
        onFinish={handleSubmit}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarStyle={{ padding: '0 24px' }}
        >
          <TabPane
            tab={
              <Space className={hasErrorsInTab('personal') ? 'ant-tabs-tab-error' : ''}>
                <FontAwesomeIcon icon={faUser} />
                Personal
              </Space>
            }
            key="personal"
            style={tabStyle}
          >
            <Card
              variant="borderless"
              title={
                <Space>
                  <FontAwesomeIcon icon={faIdCard} />
                  <Text strong>Información Personal</Text>
                </Space>
              }
              styles={{
                header: { border: 'none', padding: '0 16px 16px' },
                body: { padding: '0 16px' }
              }}
            >
              <Row gutter={16}>
                <Col span={24}>
                  <Item
                    name="nombre_matriculado"
                    label="Nombre Completo"
                    rules={[{ required: true, message: 'El nombre es requerido' }]}
                  >
                    <Input placeholder="Ingrese el nombre completo" />
                  </Item>
                </Col>
                <Col xs={24} md={12}>
                  <Item
                    name="identificacion"
                    label="Identificación"
                    rules={[{ required: true, message: 'La identificación es requerida' }]}
                  >
                    <Input placeholder="Número de documento" />
                  </Item>
                </Col>
                <Col xs={24} md={12}>
                  <Item name="tipo_documento" label="Tipo de Documento">
                    <Select placeholder="Seleccione el tipo de documento">
                      <Option value="Cédula de Ciudadanía">Cédula de Ciudadanía</Option>
                      <Option value="Cédula de Extranjería">Cédula de Extranjería</Option>
                      <Option value="Pasaporte">Pasaporte</Option>
                    </Select>
                  </Item>
                </Col>
                <Col xs={24} md={12}>
                  <Item name="sexo" label="Sexo">
                    <Select placeholder="Seleccione el sexo">
                      <Option value={1}>Masculino</Option>
                      <Option value={2}>Femenino</Option>
                    </Select>
                  </Item>
                </Col>
                <Col xs={24} md={12}>
                  <Item
                    name="edad"
                    label="Edad"
                    rules={[
                      { required: true, message: 'La edad es requerida' },
                    ]}
                  >
                    <InputNumber placeholder="Años" style={{ width: '100%' }} />
                  </Item>
                </Col>
                <Col xs={24} md={12}>
                  <Item
                    name="fecha_nacimiento"
                    label="Año de Nacimiento"
                    rules={[
                      { required: true, message: 'El año de nacimiento es requerido' }
                    ]}
                  >
                    <DatePicker
                      picker="year"
                      placeholder="Seleccione el año"
                      style={{ width: '100%' }}
                      value={form.getFieldValue('fecha_nacimiento')}
                      onChange={(date) => {
                        if (date) {
                          const añoNacimiento = date.year();
                          const añoActual = dayjs().year();
                          const edadCalculada = añoActual - añoNacimiento;
                          form.setFieldsValue({
                            edad: edadCalculada,
                            fecha_nacimiento: date
                          });
                        } else {
                          form.setFieldsValue({
                            edad: undefined,
                            fecha_nacimiento: null
                          });
                        }
                      }}
                    />
                  </Item>
                </Col>
                <Col xs={24} md={12}>
                  <Item
                    name="oid_departamento"
                    label={
                      <Space>
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        <span>Departamento</span>
                      </Space>
                    }
                  >
                    <Select
                      placeholder="Seleccione un departamento"
                      loading={loadingGeoData}
                      onChange={handleDeptoChange}
                      disabled={loadingGeoData}
                    >
                      {departamentos.map(depto => (
                        <Option key={depto.oid} value={depto.oid}>
                          {depto.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item
                    name="oid_municipio"
                    label={
                      <Space>
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        <span>Municipio</span>
                      </Space>
                    }
                    rules={[{ required: true, message: 'El municipio es requerido' }]}
                  >
                    <Select
                      placeholder="Seleccione un municipio"
                      loading={loadingGeoData}
                      disabled={!selectedDepto || loadingGeoData}
                    >
                      {municipios.map(municipio => (
                        <Option key={municipio.oid} value={municipio.oid}>
                          {municipio.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Item>
                </Col>
                <Col xs={24} md={12}>
                  <Item
                    name="oid_fc_dhaa_grupo_etnico"
                    label="Grupo Étnico"
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Select placeholder="Seleccione grupo étnico">
                      <Option value={1}>Negro(a), mulato(a), afrodescendiente, afrocolombiano(a)</Option>
                      <Option value={2}>Ningún grupo étnico</Option>
                      <Option value={3}>Indígena</Option>
                      <Option value={4}>Cravo Totumo</Option>
                      <Option value={5}>Indígena Pijao Yanuba</Option>
                      <Option value={6}>Palenquero(a)</Option>
                      <Option value={7}>Raizal del archipiélago</Option>
                      <Option value={8}>Gitano(a) o Rrom</Option>
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item
                    name="oid_ct_nombre_instancia"
                    label="Instancia"
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Select
                      placeholder="Seleccione instancia">
                      {instancias.map(inst => (
                        <Option key={inst.oid} value={inst.oid}>
                          {inst.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Item>
                </Col>
              </Row>
            </Card>
          </TabPane>
          <TabPane
            tab={
              <Space className={hasErrorsInTab('contacto') ? 'ant-tabs-tab-error' : ''}>
                <FontAwesomeIcon icon={faPhone} />
                Contacto
              </Space>
            }
            key="contacto"
            style={tabStyle}
          ><Card
            variant="borderless"
            title={
              <Space>
                <FontAwesomeIcon icon={faIdCard} />
                <Text strong>Información de Contacto</Text>
              </Space>
            }
            styles={{
              header: { border: 'none', padding: '0 16px 16px' },
              body: { padding: '0 16px' }
            }}
          >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Item
                    name="telefono"
                    label={
                      <Space>
                        <FontAwesomeIcon icon={faPhone} />
                        <span>Teléfono</span>
                      </Space>
                    }
                    rules={[{ required: true, message: 'El teléfono es requerido' }]}
                  >
                    <Input placeholder="Número de teléfono" />
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item
                    name="email"
                    label={
                      <Space>
                        <FontAwesomeIcon icon={faEnvelope} />
                        <span>Email</span>
                      </Space>
                    }
                    rules={[
                      { required: true, message: 'El email es requerido' },
                      { type: 'email', message: 'El email no es válido' }
                    ]}
                  >
                    <Input placeholder="correo@ejemplo.com" />
                  </Item>
                </Col>
              </Row>
            </Card>
          </TabPane>
          <TabPane
            tab={
              <Space>
                <FontAwesomeIcon icon={faGraduationCap} />
                Académica
              </Space>
            }
            key="academica"
            style={tabStyle}
          >
            <Card
              variant="borderless"
              title={
                <Space>
                  <FontAwesomeIcon icon={faIdCard} />
                  <Text strong>Información Académica</Text>
                </Space>
              }
              styles={{
                header: { border: 'none', padding: '0 16px 16px' },
                body: { padding: '0 16px' }
              }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Item name="cohorte" label="Cohorte">
                    <Select placeholder="Seleccione la cohorte">
                      <Option value="1">Cohorte 1</Option>
                      <Option value="2">Cohorte 2</Option>
                      <Option value="3">Cohorte 3</Option>
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item name="activo" label="Estado">
                    <Select placeholder="Seleccione el estado">
                      <Option value="Si">Activo</Option>
                      <Option value="No">Inactivo</Option>
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item name="certificado" label="Certificado">
                    <Select placeholder="Seleccione certificado">
                      <Option value="Si">Certificado</Option>
                      <Option value="No">Sin certificar</Option>
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item name="tratamiento_datos" label="Tratamiento de Datos">
                    <Select placeholder="Seleccione tratamiento de datos">
                      <Option value="Si">Acepta</Option>
                      <Option value="No">No acepta</Option>
                    </Select>
                  </Item>
                </Col>
              </Row>
            </Card>
          </TabPane>
          <TabPane
            tab={
              <Space>
                <FontAwesomeIcon icon={faFileAlt} />
                Observaciones
              </Space>
            }
            key="observaciones"
            style={tabStyle}
          >
            <Card
              variant="borderless"
              title={
                <Space>
                  <FontAwesomeIcon icon={faIdCard} />
                  <Text strong>Observaciones</Text>
                </Space>
              }
              styles={{
                header: { border: 'none', padding: '0 16px 16px' },
                body: { padding: '0 16px' }
              }}
            >
              <Item name="observacion" label="Observaciones adicionales">
                <TextArea
                  rows={6}
                  placeholder="Ingrese observaciones adicionales sobre el participante..."
                  style={{ resize: 'none' }}
                />
              </Item>
            </Card>
          </TabPane>
        </Tabs>
      </Form>
    </Modal>
  );
}